/**
 * Каркас приложения: постоянный хром вокруг сменяемого экрана.
 *
 * Ключевое решение — хром лежит СНАРУЖИ анимированного перехода. Если
 * обернуть шапку в тот же `AnimatePresence`, что и маршруты, она будет
 * перемонтироваться и мигать на каждом переходе: пользователь увидит, как
 * навигация исчезает и появляется, хотя «переехал» только экран. Здесь
 * `AppLayout` не ключуется путём, поэтому шапка и таб-бар живут между
 * маршрутами, а анимацию играет только содержимое.
 *
 * Два режима:
 *   - иммерсивный (`immersive`) — без хрома, содержимое центрируется по
 *     вертикали: приветствие, онбординг, диагностика, урок;
 *   - с хромом — шапка сверху и таб-бар снизу (на узком экране), а
 *     содержимое получает отступ под нижний бар.
 *
 * `SoundToggle` переезжает: на иммерсивных он остаётся плавающим в углу
 * (как раньше), на остальных — часть шапки.
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isPracticePath } from '../../app/navigation';
import { useThemeStore } from '../../store/themeStore';

import { AuroraGL } from '../bits/AuroraGL';
import { QuokkaCoach } from '../QuokkaCoach';
import { SoundToggle } from '../SoundToggle';
import { ThemeToggle } from '../ThemeToggle';
import { ToastHost } from '../ToastHost';
import { PaywallModal } from '../billing/PaywallModal';
import { AppHeader } from './AppHeader';
import { BackButton } from './BackButton';
import { useUiSounds } from '../../hooks/useUiSounds';
import { BottomNav } from './BottomNav';
import { IosInstallBanner } from '../install/IosInstallBanner';
import { useTranslation } from 'react-i18next';

/**
 * Сияние фона в палитре лендинга: песок → светлый песок → бирюза. Светлая
 * тема — мягкие тёплые полосы на кремовом фоне; тёмная — приглушённые
 * карамель и бирюза, которые светятся из тёплой темноты.
 */
const AURORA = {
  light: { stops: ['#E8B87A', '#F1E0BB', '#8CC4C4'], amplitude: 1.1, blend: 0.55 },
  dark: { stops: ['#B9864F', '#7A5E43', '#4F8F8F'], amplitude: 1.2, blend: 0.7 },
} as const;

interface AppLayoutProps {
  readonly immersive: boolean;
  readonly children: ReactNode;
}

export function AppLayout({ immersive, children }: AppLayoutProps) {
  const { pathname, state } = useLocation();
  // На вход пришли с приветствия — можно вернуться; из онбординга и ворот — нет: дальше только вход.
  const fromWelcome = (state as { from?: string } | null)?.from === 'welcome';
  useUiSounds();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const aurora = AURORA[useThemeStore((state) => state.mode)];

  return (
    // `overflow-x-clip`, а не `overflow-hidden`: hidden делает обёртку
    // контейнером прокрутки, и липкие шапка, футер истории и вердикт
    // дрилла перестают липнуть. clip режет вылет вбок, не трогая прокрутку
    // окна — одинаково в WebView2 (Windows) и WKWebView (macOS).
    <div className="relative flex min-h-screen min-h-dvh w-full max-w-full flex-col items-center overflow-x-clip font-sans text-heading">
      {/* Первый Tab — «перейти к содержимому»: с клавиатуры не нужно
          проходить шапку и меню на каждом экране. Кнопка, а не ссылка
          `#main`: при HashRouter якорь сменил бы маршрут. */}
      <button
        type="button"
        onClick={() => document.getElementById('main')?.focus()}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-button focus:bg-accent focus:px-5 focus:py-3 focus:font-heading focus:font-bold focus:text-on-accent"
      >
        {t('nav.skipToContent')}
      </button>
      <div className="ambient" aria-hidden="true">
        {/* На карте фон замирает: видеокарта целиком отдана 3D-мозгу. */}
        <AuroraGL colorStops={aurora.stops} amplitude={aurora.amplitude} blend={aurora.blend} speed={0.6} paused={pathname.startsWith('/map')} className="ambient-aurora" />
        <div className="ambient-grain" />
      </div>
      {immersive ? (
        // Иммерсивный экран: одна строка — «назад» слева, тема и звук справа.
        <div className="pointer-events-none absolute inset-x-0 top-0 z-40 flex h-[4.75rem] items-center justify-between gap-2 px-4 sm:px-6 lg:px-8">
          <div className="pointer-events-auto">
            {isPracticePath(pathname) && <BackButton label={t('nav.backToMap')} onClick={() => navigate('/map')} />}
            {pathname === '/login' && fromWelcome && <BackButton label={t('auth.back')} onClick={() => navigate(-1)} />}
            {/* Документы: назад — туда, откуда открыли (обычно на вход); по прямой ссылке — на приветствие. */}
            {pathname.startsWith('/legal/') && (
              <BackButton label={t('auth.back')} onClick={() => (((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0 ? navigate(-1) : navigate('/', { replace: true }))} />
            )}
          </div>
          <div className="pointer-events-auto flex items-center gap-2">
            <ThemeToggle />
            <SoundToggle />
          </div>
        </div>
      ) : (
        <AppHeader />
      )}

      <main
        id="main"
        tabIndex={-1}
        className={[
          'focus:outline-none',
          'flex w-full min-w-0 max-w-full flex-1 flex-col items-center',
          immersive
            ? // Урок прижат к верху: смена высоты шага не сдвигает заголовок.
              // Welcome/Onboarding — по центру экрана.
              [
                isPracticePath(pathname) ? 'justify-start' : 'justify-center',
                // Вход — ровно по центру: снизу тот же отступ, что сверху под строкой кнопок.
                pathname === '/login' ? 'px-4 py-24 sm:px-6 lg:px-8' : 'px-4 pb-8 pt-24 sm:px-6 lg:px-8',
              ].join(' ')
            : // Отступ снизу — под фиксированный таб-бар; на md он скрыт.
              'px-4 pb-28 pt-6 sm:px-6 sm:pt-8 md:pb-16 lg:px-8',
        ].join(' ')}
      >
        {/* iOS не показывает системное окно установки — подсказка над содержимым. */}
        {!immersive && <IosInstallBanner />}
        {children}
      </main>

      {!immersive && <BottomNav />}

      <QuokkaCoach />

      {/* Уведомления — глобальны и живут вне анимации перехода. */}
      <ToastHost />
      {/* Лимит тарифа исчерпан — модалка поверх любого экрана. */}
      <PaywallModal />
    </div>
  );
}
